/**
 * Shared visual vocabulary for the Cabinet graph view.
 *
 * One place so the ECharts option object and the Tailwind legend/side panel can
 * never drift apart — a legend swatch that disagrees with the rendered node
 * colour is worse than no legend.
 */

import type {
	CabinetEdgeType,
	CabinetNodeKind,
	CabinetObjectStatus,
} from "../../types/cabinet";

export interface NodeKindStyle {
	label: string;
	/** Node fill; deal sits on the brand dark teal, documents on the mid-tone. */
	color: string;
	symbolSize: number;
	description: string;
}

export const NODE_KIND_STYLES: Record<CabinetNodeKind, NodeKindStyle> = {
	deal: {
		label: "Deal",
		color: "#004C4B",
		symbolSize: 46,
		description: "One deal room",
	},
	document: {
		label: "Document",
		color: "#008782",
		symbolSize: 26,
		description: "Logical document (version chain inside)",
	},
	entity: {
		label: "Entity",
		color: "#B45309",
		symbolSize: 18,
		description: "Borrower, facility, sponsor…",
	},
};

export interface EdgeTypeStyle {
	label: string;
	color: string;
	/**
	 * ECharts lineStyle.type. A number[] is a custom dash pattern
	 * ([dash, gap, …]) — used where the three named patterns are all taken.
	 */
	type: "solid" | "dashed" | "dotted" | number[];
	description: string;
}

export const EDGE_TYPE_STYLES: Record<CabinetEdgeType, EdgeTypeStyle> = {
	belongs_to: {
		label: "belongs_to",
		color: "#94A3B8",
		type: "solid",
		description: "Filed under a deal",
	},
	member_of_family: {
		label: "member_of_family",
		color: "#0F766E",
		type: "dashed",
		description: "Same periodic series",
	},
	references: {
		label: "references",
		color: "#B45309",
		type: "dotted",
		description: "Names an entity",
	},
	same_content: {
		label: "same content",
		/**
		 * Violet: unused elsewhere in the graph, and far enough from the teals
		 * (deal/document/member_of_family) and the amber (entity/references)
		 * to stay separable for the common red-green deficiencies. Contrast
		 * against the white canvas is ~7:1.
		 */
		color: "#7C3AED",
		/**
		 * Long dash + short gap + dot: a "chain link" pattern that reads as
		 * distinct from dashed (member_of_family) and dotted (references) even
		 * in a screenshot, which is where these graphs usually get discussed.
		 */
		type: [10, 4, 2, 4],
		description: "Byte-identical, canonical in another deal",
	},
	entity_group: {
		label: "same family",
		/** Indigo — inferred, and distinct from the violet of same_content. */
		color: "#4F46E5",
		/** Short dash + long gap: reads as tentative next to the solid facts. */
		type: [3, 5],
		description: "Proposed umbrella over two entities",
	},
	suggested_home: {
		label: "suggested home",
		/** Rose: the only warm inferred line, because it asks for a decision. */
		color: "#E11D48",
		/** Long dash, wide gap — visibly a proposal, not a filing. */
		type: [12, 6],
		description: "Where a misfiled document probably belongs",
	},
};

/**
 * SVG `stroke-dasharray` for an edge style, so the legend swatch draws the
 * SAME pattern the chart does. The legend used to approximate each pattern with
 * a Tailwind border class, which is how a swatch drifts from the line it claims
 * to describe — and a custom dash array cannot be expressed that way at all.
 */
export function edgeDashArray(style: EdgeTypeStyle): string | undefined {
	if (Array.isArray(style.type)) return style.type.join(" ");
	if (style.type === "dashed") return "6 4";
	if (style.type === "dotted") return "1 4";
	return undefined;
}

/** Tailwind classes for the current/superseded/duplicate status chip. */
export const STATUS_CHIP_CLASSES: Record<CabinetObjectStatus, string> = {
	current: "bg-emerald-50 text-emerald-700 border-emerald-200",
	superseded: "bg-amber-50 text-amber-700 border-amber-200",
	duplicate: "bg-gray-100 text-gray-600 border-gray-300",
};
